// Quiz modal: shows questions from the QuestionEngine with big Kahoot-style buttons,
// visuals (clocks, fractions, shapes), read-aloud, streaks and friendly explanations.
import { blockHint } from './learning-aids.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { escapeHtml } from './util.js';
import { ladderRung } from './questions.js';

const PRAISE = (name) => [`You worked it out, ${name}!`, 'Great thinking!', 'You found the answer!', 'Nicely solved!', 'Keep exploring!', `Well done, ${name}!`];
const ENCOURAGE = (name) => [`Nice try, ${name}!`, 'So close!', 'Good try!', 'Almost!', 'Not quite — you’ve got this!'];
const CHALLENGE_TRY = (name) => ['Challenges are meant to be tricky!', `Brave try, ${name}!`, 'That was a tough one. Great try!'];
// The challenge question's gold frame and label (inline, so the quiz needs no extra stylesheet rules).
const CHALLENGE_GLOW = 'inset 0 2px 0 rgba(255,255,255,.12), 0 0 0 3px rgba(255,210,63,.55), 0 0 32px rgba(255,190,40,.55), 0 10px 0 rgba(0,0,0,.25)';
const CHALLENGE_TAG = '<span class="quiz-challenge" style="color:var(--gold)">🔥 Challenge · worth a gem</span>';
const oneOf = (list) => list[Math.floor(Math.random() * list.length)];
const COLORS = ['red', 'blue', 'yellow', 'green'];
const SHAPES = ['▲', '◆', '●', '■'];

const fmtChoice = (c) => {
  const m = /^(\d+)\/(\d+)$/.exec(c);
  if (m) return `<span class="frac"><span>${m[1]}</span><span>${m[2]}</span></span>`;
  return escapeHtml(c);
};

export class Quiz {
  constructor(root, engine, getSave, onStatsChange) {
    this.engine = engine;
    this.getSave = getSave;
    this.onStats = onStatsChange;
    this.streak = 0;
    this.open = false;
    this.el = document.createElement('div');
    this.el.id = 'quiz';
    this.el.className = 'modal-wrap';
    this.el.innerHTML = `
      <div class="quiz-panel panel">
        <div class="quiz-head">
          <canvas class="quiz-avatar" width="64" height="64"></canvas>
          <div class="quiz-head-text">
            <div class="quiz-title">Brain Challenge</div>
            <div class="quiz-sub">Solve it to continue!</div>
          </div>
          <div class="quiz-pips"></div>
        </div>
        <div class="quiz-body">
          <div class="quiz-prompt"></div>
          <div class="quiz-qrow">
            <div class="quiz-q"></div>
            <button class="quiz-speak" title="Read it to me">🔊</button>
          </div>
          <canvas class="quiz-visual" width="440" height="300"></canvas>
          <div class="quiz-choices"></div>
          <button class="quiz-hint btn btn-small btn-dark" aria-label="Show a learning hint">💡 Give me a hint</button><div class="quiz-hint-text" role="status"></div><div class="quiz-feedback" aria-live="polite"></div>
        </div>
        <div class="quiz-foot">
          <div class="quiz-streak"></div>
          <button class="quiz-later btn btn-small btn-dark">Later</button>
        </div>
      </div>`;
    root.appendChild(this.el);
    this.$ = (s) => this.el.querySelector(s);
    this.$('.quiz-speak').setAttribute('aria-label', 'Read the question aloud');
    this.$('.quiz-speak').addEventListener('click', () => this.speak(true));
    this.$('.quiz-later').addEventListener('click', () => this.cancel && this.cancel());
    window.addEventListener('keydown', (e) => {
      if (!this.open || !this.answerHandler) return;
      const i = ['1', '2', '3', '4'].indexOf(e.key);
      if (i >= 0) this.answerHandler(i);
    });
  }

  setAvatar(canvas) {
    const ctx = this.$('.quiz-avatar').getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 64, 64);
    ctx.drawImage(canvas, 8, 8, 8, 8, 0, 0, 64, 64);
    ctx.drawImage(canvas, 40, 8, 8, 8, 0, 0, 64, 64);
  }

  speak(force) {
    if (!this.q) return;
    const s = this.getSave().settings;
    if (!force && !s.readAloud && this.q.topic !== 'spell') return;
    const text = this.q.speakText || this.q.text.replace(/<[^>]+>/g, '').replace('?', ' what');
    audio.speak(text);
  }

  // opts: {title, sub, needed, weights, tables, allowLater, onCorrect(k), boost, ladder, selfPaced}
  // ladder (Explore lessons): one boost per question, e.g. [-1, 0, 1]; the last rung is the 🔥 challenge.
  // Resolves {asked, firstTry, correct, xp, cancelled, topicUps, challengeWon, perfect}.
  run(opts) {
    return new Promise((resolve) => {
      const session = this.session = (this.session || 0) + 1;
      this.open = true;
      this.el.classList.add('show');
      document.body.classList.add('quiz-open');
      audio.setDuck(true);
      this.$('.quiz-title').textContent = opts.title || 'Brain Challenge';
      this.$('.quiz-sub').innerHTML = opts.sub || (opts.needed > 1 ? `Answer <b>${opts.needed}</b> to finish!` : 'Answer to continue!');
      this.$('.quiz-later').style.display = opts.allowLater ? '' : 'none';
      const res = { asked: 0, firstTry: 0, correct: 0, xp: 0, cancelled: false, topicUps: [], challengeWon: false, perfect: false };
      const needed = opts.needed || 1;
      const nextQuestion = () => {
        const rung = opts.ladder ? ladderRung(opts.ladder, res.correct, needed) : null;
        const q = this.engine.next({ weights: opts.weights, tables: opts.tables, boost: rung ? rung.boost : opts.boost });
        if (rung?.challenge) q.challenge = true;
        return q;
      };
      const finish = () => {
        // Perfect: every answer right first time, without a hint.
        res.perfect = !res.cancelled && res.correct >= needed && res.firstTry >= needed;
        this.open = false;
        this.answerHandler = null;
        this.cancel = null;
        this.el.classList.remove('show');
        document.body.classList.remove('quiz-open');
        audio.setDuck(false);
        if ('speechSynthesis' in window) speechSynthesis.cancel();
        resolve(res);
      };
      this.cancel = () => {
        res.cancelled = true;
        finish();
      };
      const renderPips = () => {
        this.$('.quiz-pips').innerHTML = needed > 1 ? `<small>${Math.min(res.correct + 1, needed)} of ${needed}</small>` + Array.from({ length: needed }, (_, i) => `<span class="pip ${i < res.correct ? 'on' : ''}"></span>`).join('') : '';
      };
      const ask = (q, retry) => {
        if (!this.open || this.session !== session) return;
        let hinted = false;
        const hint = this.$('.quiz-hint'), hintText = this.$('.quiz-hint-text');
        hint.disabled = false; hintText.textContent = '';
        hint.onclick = () => {
          if (!this.answerHandler) return;
          hinted = true; hint.disabled = true; hintText.textContent = q.hint || 'Take it one step at a time. You can do this!';
          const aid=blockHint(q);if(aid){const canvas=this.$('.quiz-visual');canvas.style.display='block';drawVisual(canvas,aid);hintText.textContent=aid.label;}
        };
        this.q = q;
        renderPips();
        // The challenge keeps its gold frame only while the gem is still there to win.
        const challenge = !!q.challenge && !retry, panel = this.$('.quiz-panel');
        panel.classList.toggle('challenge', challenge);
        panel.style.borderColor = challenge ? 'var(--gold)' : '';
        panel.style.boxShadow = challenge ? CHALLENGE_GLOW : '';
        if (challenge) this.$('.quiz-prompt').innerHTML = CHALLENGE_TAG + (q.prompt ? ` · ${escapeHtml(q.prompt)}` : '');
        else this.$('.quiz-prompt').textContent = retry ? 'Have another go! 💪' : q.prompt || '';
        const qe = this.$('.quiz-q');
        qe.innerHTML = q.text;
        qe.classList.toggle('word', !!q.word || q.text.length > 46);
        qe.classList.toggle('spell', q.topic === 'spell');
        const vis = this.$('.quiz-visual');
        if (q.visual) {
          vis.style.display = 'block';
          drawVisual(vis, q.visual);
        } else vis.style.display = 'none';
        const fb = this.$('.quiz-feedback');
        fb.className = 'quiz-feedback';
        fb.innerHTML = '';
        const ch = this.$('.quiz-choices');
        const order = retry ? shuffleCopy(q.choices) : q.choices;
        this.order = order;
        ch.innerHTML = order
          .map((c, i) => `<button class="choice c-${COLORS[i]} ${q.topic === 'spell' ? 'spellc' : ''}" data-i="${i}"><span class="cshape">${SHAPES[i]}</span><span class="ctext">${fmtChoice(c)}</span><span class="ckey">${i + 1}</span></button>`)
          .join('');
        const buttons = [...ch.querySelectorAll('.choice')];
        buttons.forEach((b) => b.addEventListener('click', () => this.answerHandler && this.answerHandler(Number(b.dataset.i))));
        this.el.querySelector('.quiz-panel').classList.remove('pop');
        void this.el.offsetWidth;
        this.el.querySelector('.quiz-panel').classList.add('pop');
        if (!retry) setTimeout(() => { if (this.open && this.session === session) this.speak(false); }, 250);
        this.answerHandler = (i) => {
          this.answerHandler = null;
          const pick = order[i];
          const correct = pick === q.answer;
          const btn = buttons[i];
          if (!retry) {
            res.asked++;
            const up = this.engine.record(q, correct, !hinted);
            if (up) res.topicUps.push(q.topic);
          }
          if (correct) {
            res.correct++;
            const won = !!q.challenge && !retry && !hinted;
            if (won) res.challengeWon = true;
            if (!retry && !hinted) {
              res.firstTry++;
              this.streak++;
            }
            if (hinted) this.streak = 0;
            const gain = retry || hinted ? 4 : 10 + Math.min(this.streak, 10) * 2;
            res.xp += gain;
            this.onStats && this.onStats({ correct: true, firstTry: !retry && !hinted, firstAttempt: !retry, streak: this.streak, challenge: !!q.challenge });
            btn.classList.add('right');
            buttons.forEach((b) => b !== btn && b.classList.add('dim'));
            audio.play('correct');
            haptics.play(won ? 'gem' : this.streak >= 3 && !retry ? 'combo' : 'correct');
            if (won) setTimeout(() => audio.play('gem'), 280);
            else if (this.streak >= 3 && !retry) setTimeout(() => audio.play('combo', { streak: this.streak }), 280);
            fb.className = 'quiz-feedback good show';
            fb.innerHTML = `<div class="fb-big">${oneOf(PRAISE(escapeHtml(this.getSave().name)))}</div><div class="fb-xp">+${gain} XP${won ? ' · 🔥 Challenge won!' : this.streak >= 3 && !retry ? ` · 🔥 ${this.streak} in a row!` : ''}</div>`;
            this.updateStreak();
            opts.onCorrect && opts.onCorrect(res.correct);
            // All answered: Later would throw away the finished lesson's stamp, chest and gems.
            if (res.correct >= needed) this.$('.quiz-later').style.display = 'none';
            const next = () => {
              if (!this.open || this.session !== session) return;
              if (res.correct >= needed) finish();
              else ask(nextQuestion(), false);
            };
            if (opts.selfPaced) {
              fb.innerHTML += `<div class="earned-blocks">🧱 +8 blocks for your next build!</div><div class="fb-explain">${q.explain}</div><button class="btn btn-green quiz-next">${res.correct >= needed ? 'Back to exploring 🌱' : 'Next discovery ➜'}</button>`;
              fb.querySelector('.quiz-next').addEventListener('click', next, { once: true });
            } else setTimeout(next, res.correct >= needed ? 1000 : 1100);
          } else {
            if (!retry) this.streak = 0;
            this.onStats && this.onStats({ correct: false, firstTry: false, firstAttempt: !retry, streak: 0, challenge: !!q.challenge });
            btn.classList.add('wrong');
            const ri = order.indexOf(q.answer);
            setTimeout(() => buttons[ri].classList.add('right'), 350);
            buttons.forEach((b, k) => k !== i && k !== ri && b.classList.add('dim'));
            audio.play('wrong');
            haptics.play('wrong');
            this.updateStreak();
            fb.className = 'quiz-feedback bad show';
            fb.innerHTML = `<div class="fb-big">${oneOf((q.challenge && !retry ? CHALLENGE_TRY : ENCOURAGE)(escapeHtml(this.getSave().name)))}</div><div class="fb-explain">${q.explain}</div><button class="btn btn-blue fb-ok">Got it! 👍</button>`;
            const ok = fb.querySelector('.fb-ok');
            ok.disabled = true;
            setTimeout(() => (ok.disabled = false), 900);
            ok.addEventListener('click', () => {
              audio.play('click');
              ask(q, true);
            });
          }
        };
      };
      this.updateStreak();
      ask(nextQuestion(), false);
    });
  }

  updateStreak() {
    const el = this.$('.quiz-streak');
    el.innerHTML = this.streak >= 2 ? `<span class="flame">🔥</span> Streak <b>${this.streak}</b>` : '';
    el.classList.toggle('hot', this.streak >= 5);
  }
}

function shuffleCopy(a) {
  const b = a.slice();
  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
}

// ---------------- visuals ----------------
export function drawVisual(canvas, v) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if(v.kind==='groups') {
    const size=Math.min(28,(W-40)/v.cols,(H-30)/v.rows),x0=(W-v.cols*size)/2,y0=(H-v.rows*size)/2;
    for(let r=0;r<v.rows;r++)for(let c=0;c<v.cols;c++){
      ctx.fillStyle=['#83d67a','#ffd06d','#84c9f0'][r%3];ctx.fillRect(x0+c*size+2,y0+r*size+2,size-4,size-4);
      ctx.fillStyle='#ffffff44';ctx.fillRect(x0+c*size+2,y0+r*size+2,size-4,3);
    }
  }
  else if (v.kind === 'clock') drawClock(ctx, W / 2, H / 2, Math.min(W, H) * 0.46, v.h, v.m);
  else if (v.kind === 'fraction') drawFraction(ctx, W, H, v);
  else if (v.kind === 'shape') drawShape(ctx, W, H, v);
  else if (v.kind === 'shape3d') drawShape3d(ctx, W, H, v.name, v.hidden);
}

function drawClock(ctx, cx, cy, R, h, m) {
  ctx.fillStyle = '#2b2f45';
  ctx.beginPath();
  ctx.arc(cx, cy + 6, R + 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffd23f';
  ctx.beginPath();
  ctx.arc(cx, cy, R + 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();
  for (let i = 0; i < 60; i++) {
    const a = (i / 60) * Math.PI * 2;
    const big = i % 5 === 0;
    ctx.strokeStyle = big ? '#2b2f45' : '#9aa4b2';
    ctx.lineWidth = big ? 4 : 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.sin(a) * (R - (big ? 14 : 8)), cy - Math.cos(a) * (R - (big ? 14 : 8)));
    ctx.lineTo(cx + Math.sin(a) * (R - 3), cy - Math.cos(a) * (R - 3));
    ctx.stroke();
  }
  ctx.fillStyle = '#2b2f45';
  ctx.font = `${Math.round(R * 0.2)}px "Lilita One", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let i = 1; i <= 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    ctx.fillText(String(i), cx + Math.sin(a) * R * 0.72, cy - Math.cos(a) * R * 0.72 + 2);
  }
  const ha = (((h % 12) + m / 60) / 12) * Math.PI * 2;
  const ma = (m / 60) * Math.PI * 2;
  ctx.strokeStyle = '#2b2f45';
  ctx.lineWidth = 11;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.sin(ha) * R * 0.48, cy - Math.cos(ha) * R * 0.48);
  ctx.stroke();
  ctx.strokeStyle = '#ff4d6d';
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.sin(ma) * R * 0.8, cy - Math.cos(ma) * R * 0.8);
  ctx.stroke();
  ctx.fillStyle = '#2b2f45';
  ctx.beginPath();
  ctx.arc(cx, cy, 9, 0, Math.PI * 2);
  ctx.fill();
}

function drawFraction(ctx, W, H, v) {
  const fill = '#4d9de0', empty = '#eef2f7', line = '#2b2f45';
  if (v.shape === 'circle') {
    const cx = W / 2, cy = H / 2, R = Math.min(W, H) * 0.42;
    for (let i = 0; i < v.parts; i++) {
      const a0 = (i / v.parts) * Math.PI * 2 - Math.PI / 2, a1 = ((i + 1) / v.parts) * Math.PI * 2 - Math.PI / 2;
      ctx.fillStyle = i < v.shaded ? fill : empty;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, a0, a1);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = line;
      ctx.lineWidth = 5;
      ctx.stroke();
    }
    return;
  }
  const bw = W * 0.86, bh = H * 0.38, x0 = (W - bw) / 2, y0 = (H - bh) / 2;
  const pw = bw / v.parts;
  for (let i = 0; i < v.parts; i++) {
    ctx.fillStyle = i < v.shaded ? fill : empty;
    ctx.fillRect(x0 + i * pw, y0, pw, bh);
    ctx.strokeStyle = line;
    ctx.lineWidth = 5;
    ctx.strokeRect(x0 + i * pw, y0, pw, bh);
  }
}

function drawShape(ctx, W, H, v) {
  const cx = W / 2, cy = H / 2 + 6, R = Math.min(W, H) * 0.4;
  ctx.fillStyle = '#7be35a';
  ctx.strokeStyle = '#2b2f45';
  ctx.lineWidth = 6;
  ctx.beginPath();
  if (v.name === 'rectangle') ctx.rect(cx - R * 1.3, cy - R * 0.7, R * 2.6, R * 1.4);
  else if (v.name === 'square') ctx.rect(cx - R * 0.8, cy - R * 0.8, R * 1.6, R * 1.6);
  else if (v.name === 'right-angled triangle') {
    ctx.moveTo(cx - R * 1.1, cy + R * 0.75);
    ctx.lineTo(cx - R * 1.1, cy - R * 0.85);
    ctx.lineTo(cx + R * 1.1, cy + R * 0.75);
  }
  else
    for (let i = 0; i < v.sides; i++) {
      const a = (i / v.sides) * Math.PI * 2 - Math.PI / 2 + (v.sides % 2 ? 0 : Math.PI / v.sides);
      const x = cx + Math.cos(a) * R, y = cy + Math.sin(a) * R;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

// hidden: also draw the edges at the back as dashed lines, for counting edges and vertices.
function drawShape3d(ctx, W, H, name, hidden = false) {
  const cx = W / 2, cy = H / 2;
  ctx.lineWidth = 5;
  ctx.strokeStyle = '#2b2f45';
  const poly = (pts, col) => {
    ctx.fillStyle = col;
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  };
  const dashed = (...pts) => {
    ctx.save();
    ctx.setLineDash([10, 9]);
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
    ctx.restore();
  };
  if (name === 'pyramid') {
    const top = [cx, cy - 110], a = [cx - 110, cy + 60], b = [cx + 40, cy + 95], c = [cx + 120, cy + 45], d = [cx - 30, cy + 10];
    poly([top, a, b], '#ffd23f');
    poly([top, b, c], '#e0a810');
    if (hidden) {
      dashed(a, d, c);
      dashed(d, top);
    }
    return;
  }
  if (name === 'prism') {
    // A triangular prism: the front triangle, the sloping side, and the back corner hidden behind them.
    const D = [110, -50], A = [cx - 150, cy + 80], B = [cx + 10, cy + 80], C = [cx - 70, cy - 60];
    const back = (p) => [p[0] + D[0], p[1] + D[1]], A2 = back(A), B2 = back(B), C2 = back(C);
    poly([A, B, C], '#ffd23f');
    poly([B, B2, C2, C], '#e0a810');
    if (hidden) {
      dashed(A, A2, B2);
      dashed(A2, C2);
    }
    return;
  }
  if (name === 'tetra') {
    // A triangular-based pyramid: two faces at the front, the base edge at the back hidden.
    const A = [cx - 120, cy + 70], B = [cx + 50, cy + 95], D = [cx + 120, cy + 20], C = [cx - 10, cy - 115];
    poly([C, A, B], '#ffd23f');
    poly([C, B, D], '#e0a810');
    if (hidden) dashed(A, D);
    return;
  }
  if (name === 'triangle') {
    poly([[cx, cy - 100], [cx - 115, cy + 90], [cx + 115, cy + 90]], '#7be35a');
    return;
  }
  const w = name === 'cuboid' ? 170 : 115, h = 115, d = 60;
  const x0 = cx - w / 2 - d / 2, y0 = cy - h / 2 + d / 2;
  poly([[x0, y0], [x0 + w, y0], [x0 + w + d, y0 - d], [x0 + d, y0 - d]], '#8ee86a');
  poly([[x0 + w, y0], [x0 + w + d, y0 - d], [x0 + w + d, y0 - d + h], [x0 + w, y0 + h]], '#4e9a32');
  poly([[x0, y0], [x0 + w, y0], [x0 + w, y0 + h], [x0, y0 + h]], '#6ac74a');
  if (hidden) {
    const k = [x0 + d, y0 - d + h];
    dashed([x0 + d, y0 - d], k, [x0 + w + d, y0 - d + h]);
    dashed(k, [x0, y0 + h]);
  }
}
